// DTOs de WS-8 P2 — Cruce / Conciliación (módulo Facturación del coordinador).
//
// v2 (feedback coordinador 2026-07-01, espejo del Excel "Seguimiento semanal"):
//   - Prod. Acumulada EN VIVO: avanza con cada captura del líder; se expone
//     también cuánto está VALIDADO por supervisor (la facturación P4 usa solo
//     lo validado).
//   - Estados como el Excel: sin_programar · en_proceso · terminada.
//     Terminada = automática cuando capturado ≥ programado, o CIERRE manual
//     con motivo cuando quedó corta (el "TERMINADA al 73%" del Excel).
//   - Al cerrar se pueden seleccionar los PAROS que el líder registró en esa
//     OP (causa interna, análisis futuro) y/o unidades no entregadas por el
//     cliente (alimentan el Cumplimiento REAL). Reabrir deshace el cierre.
//
// SOLO LÍNEAS (Q5): las órdenes de tienda usan numeración 4503… de 10 díg.
// Capturas con OP NULL no cruzan. programada = 0 → cumplimiento NULL.
//
// Endpoints:
//   GET    /api/coordinador/facturacion/cruce?desde&hasta&cliente_id&area_id
//   GET    /api/coordinador/facturacion/cruce/detalle?...clave del grupo
//   PATCH  /api/coordinador/facturacion/cruce/cerrar
//   DELETE /api/coordinador/facturacion/cruce/cerrar   (reabrir)
import { z } from 'zod';
import { opLoteOcRegex, opLoteOcMensaje } from './registro_produccion_hora.js';
/** Causa del cierre — derivada en el server: unidades del cliente > 0 →
 * 'cliente' (alimenta el REAL); si no → 'interna' (solo documenta). */
export const causaConciliacionSchema = z.enum(['cliente', 'interna']);
export const cruceEstadoSchema = z.enum(['sin_programar', 'en_proceso', 'terminada']);
/** Paro del líder referenciado al cerrar (snapshot para análisis futuro). */
export const paroConciliacionSchema = z.object({
    registro_paro_id: z.string().uuid(),
    fecha: z.string().date(),
    causal_madre: z.string(),
    causal: z.string(),
    minutos: z.number(),
});
// =============================================================================
// Query
// =============================================================================
export const facturacionCruceQuerySchema = z.object({
    desde: z.string().date(),
    hasta: z.string().date(),
    cliente_id: z.string().uuid().optional(),
    area_id: z.string().uuid().optional(),
});
/** Clave de un grupo del cruce (cliente, planta, OP, SKU). */
const claveGrupoSchema = z.object({
    cliente_id: z.string().uuid(),
    planta_id: z.string().uuid(),
    /** NULL = grupo de programación sin OP. */
    numero_op: z.string().trim().regex(opLoteOcRegex, opLoteOcMensaje).nullable(),
    sku_id: z.string().uuid(),
});
// =============================================================================
// Fila del cruce — una por (cliente, planta, OP, SKU)
// =============================================================================
export const cruceFilaSchema = z.object({
    cliente_id: z.string().uuid(),
    cliente_nombre: z.string(),
    planta_id: z.string().uuid(),
    planta_nombre: z.string(),
    area_id: z.string().uuid(),
    area_codigo: z.string(),
    area_nombre: z.string(),
    numero_op: z.string().nullable(),
    sku_id: z.string().uuid(),
    sku_codigo_cliente: z.string(),
    sku_descripcion: z.string(),
    fecha_programada_desde: z.string().date().nullable(),
    fecha_programada_hasta: z.string().date().nullable(),
    /** NULL si sin_programar. */
    cantidad_programada: z.number().nullable(),
    /** Prod. acumulada EN VIVO (toda captura válida del líder). */
    cantidad_capturada: z.number(),
    /** Parte de la acumulada ya validada por supervisor (base de facturación). */
    cantidad_validada: z.number(),
    /** capturada − programada (solo con programación). */
    diferencia: z.number().nullable(),
    /** capturada/programada ×100. NULL si programada=0 o sin programación. */
    cumplimiento_pct: z.number().nullable(),
    /** (capturada + no_entregada_cliente)/programada ×100. NULL ídem. */
    cumplimiento_real_pct: z.number().nullable(),
    estado: cruceEstadoSchema,
    /** TRUE si la terminó un cierre manual (con motivo); FALSE si es automática. */
    cerrada_manual: z.boolean(),
    observacion: z.string().nullable(),
    observacion_conciliacion: z.string().nullable(),
    causa_conciliacion: causaConciliacionSchema.nullable(),
    motivo_conciliacion: z.string().nullable(),
    paros_conciliacion: z.array(paroConciliacionSchema).nullable(),
    no_entregada_justificada: z.number(),
    conciliado_at: z.string().nullable(),
});
// =============================================================================
// Response GET
// =============================================================================
export const facturacionCruceSchema = z.object({
    desde: z.string().date(),
    hasta: z.string().date(),
    kpis: z.object({
        ops_programadas: z.number().int().min(0),
        terminadas: z.number().int().min(0),
        en_proceso: z.number().int().min(0),
        sin_programar: z.number().int().min(0),
        unidades_programadas: z.number(),
        unidades_capturadas: z.number(),
        unidades_validadas: z.number(),
        /** Σcapturada/Σprogramada ×100 (grupos con programación). NULL sin datos. */
        cumplimiento_global_pct: z.number().nullable(),
    }),
    filas: z.array(cruceFilaSchema),
    clientes: z.array(z.object({ id: z.string().uuid(), nombre: z.string(), es_principal: z.boolean() })),
    areas: z.array(z.object({
        id: z.string().uuid(),
        codigo: z.string(),
        nombre: z.string(),
        planta_id: z.string().uuid(),
        planta_nombre: z.string(),
    })),
});
// =============================================================================
// GET detalle de un grupo — modal "Ver detalle"
// =============================================================================
export const cruceDetalleQuerySchema = claveGrupoSchema.extend({
    desde: z.string().date(),
    hasta: z.string().date(),
});
export const cruceDetalleSchema = z.object({
    /** Primera y última captura del grupo en el rango (NULL sin capturas). */
    fecha_inicio: z.string().date().nullable(),
    fecha_fin: z.string().date().nullable(),
    /** Turnos en los que se trabajó la OP. */
    turnos: z.array(z.string()),
    /** Capturas agregadas por fecha + turno. */
    capturas: z.array(z.object({
        fecha: z.string().date(),
        turno_nombre: z.string(),
        linea_codigo: z.string(),
        cantidad: z.number(),
        cantidad_validada: z.number(),
        bloques: z.number().int(),
    })),
    /** Paros del líder durante las capturas de esta OP. */
    paros: z.array(z.object({
        registro_paro_id: z.string().uuid(),
        fecha: z.string().date(),
        causal_madre: z.string(),
        causal: z.string(),
        minutos: z.number(),
        observaciones: z.string().nullable(),
    })),
    /** Observaciones de programación (todas las filas del grupo). */
    observaciones_programacion: z.array(z.string()),
    conciliacion: z
        .object({
        observacion: z.string().nullable(),
        causa: causaConciliacionSchema.nullable(),
        motivo: z.string().nullable(),
        no_entregada_justificada: z.number(),
        conciliado_at: z.string().nullable(),
        paros: z.array(paroConciliacionSchema).nullable(),
    })
        .nullable(),
});
// =============================================================================
// PATCH cerrar — cierre manual con motivo (paros del líder y/o cliente)
// =============================================================================
export const cruceCerrarSchema = claveGrupoSchema.extend({
    observacion_conciliacion: z
        .string()
        .trim()
        .min(5, 'La observación debe tener al menos 5 caracteres')
        .max(500),
    /** Unidades NO entregadas por el cliente (alimentan el REAL). 0 = nada
     * atribuible al cliente → el server deriva causa interna. */
    no_entregada_justificada: z.number().nonnegative(),
    /** Paros del líder seleccionados como causa (snapshot). */
    paros: z.array(paroConciliacionSchema).max(50).nullable().optional(),
    /** Motivo resumido; si no viene, el server lo arma con las causales. */
    motivo: z.string().trim().max(300).nullable().optional(),
});
export const cruceCerrarResultSchema = z.object({
    actualizadas: z.number().int().min(0),
});
/** DELETE cerrar (reabrir): misma clave del grupo. */
export const cruceReabrirSchema = claveGrupoSchema;
//# sourceMappingURL=coordinador_facturacion_cruce.js.map